import * as React from 'react';
import { RadioGroup as MuiRadioGroup, FormLabel, FormHelperText, FormControl, Collapse, makeStyles } from '@material-ui/core';
import { OtherTextField } from 'components/common/OtherTextField/OtherTextField';
import RadioButton from '../RadioButton/RadioButton';

const useStyles = makeStyles(() => {
  return {
    radioGroupContainer: {
      width: '90%',
      '& .threepp-radio-button-wrapper': {
        width: 'max-content',
        maxWidth: '100%',
        display: 'inline-flex',
        alignItems: 'start'
      }
    }
  };
});

export const RadioGroup = props => {
  const { radioGroupContainer } = useStyles();
  const {
    options,
    selected: initialSelected,
    disabled,
    title,
    row,
    otherText: initialOtherText,
    onChange,
    error: initialError,
    onValidationError,
    otherTextMaxCharacters
  } = props;

  // State Management
  const [selectedValue, setSelectedValue] = React.useState(initialSelected);
  const [otherText, setOtherText] = React.useState(initialOtherText || '');
  const [otherTextError, setOtherTextError] = React.useState(!initialOtherText);
  const [error, setError] = React.useState(initialError);

  // Local Vars
  const possibleValueWithOtherText =
    options.find(possibleValue => {
      return possibleValue.enableOtherText;
    }) || {};
  const showOtherText = selectedValue === possibleValueWithOtherText.value;

  // Side Effects
  React.useEffect(() => {
    setSelectedValue(initialSelected);
  }, [initialSelected]);

  React.useEffect(() => {
    setOtherText(initialOtherText);
    setOtherTextError(!initialOtherText);
  }, [initialOtherText]);

  // Update error if error prop changes
  React.useEffect(() => {
    setError(initialError);
  }, [initialError]);

  // Event Handlers
  const handleValueChange = React.useCallback(
    e => {
      const { value } = e.target;
      const isNotNumber = isNaN(value);
      const radioButtonValue = isNotNumber ? value : Number(value);
      setSelectedValue(radioButtonValue);
      const otherTextIsRequired = possibleValueWithOtherText.value === value;
      const otherTextIsMissing = otherTextError && otherTextIsRequired;

      if (otherTextIsMissing) {
        onValidationError(radioButtonValue, otherTextIsRequired ? otherText : null);
      } else if (onChange) {
        onChange(radioButtonValue, otherTextIsRequired ? otherText : null);
      }
    },
    [onChange, onValidationError, otherText, otherTextError, possibleValueWithOtherText.value]
  );
  const handleOtherTextChange = React.useCallback(
    e => {
      const { value } = e.target;
      setOtherTextError(!value);
      setOtherText(value);
      if (!value) {
        onValidationError(selectedValue, value);
      } else {
        onChange(selectedValue, value);
      }
    },
    [onChange, onValidationError, selectedValue]
  );

  // Effects

  // Render
  const radioButtons = options.map(option => {
    const { value, label, enableOtherText, defaultOtherText } = option;
    const isSelected = value === selectedValue;
    return (
      <React.Fragment key={value}>
        <RadioButton disabled={disabled} key={value} value={value} label={label} selected={isSelected} onChange={handleValueChange} />
        {enableOtherText && (
          <Collapse in={showOtherText}>
            <OtherTextField
              value={otherText}
              onChange={handleOtherTextChange}
              label={defaultOtherText}
              required
              error={otherTextError}
              disabled={disabled}
              maxCharactersAllowed={otherTextMaxCharacters}
            />
          </Collapse>
        )}
      </React.Fragment>
    );
  });

  return (
    <>
      <FormControl className={radioGroupContainer}>
        {title && <FormLabel>{title}</FormLabel>}
        <MuiRadioGroup row={row}>{radioButtons}</MuiRadioGroup>
      </FormControl>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
};
